import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { GATES } from "@/data/temple";
import { useAuth } from "@/lib/auth";
import { slugify } from "@/lib/intro";
import { Reveal } from "@/components/temple/Reveal";
import { ease } from "@/lib/scroll";
import { ReflectionForm, ReflectionWall } from "@/components/temple/Reflections";
import CheckoutCard from "@/components/temple/CheckoutCard";
import QuantumOffer from "@/components/quantum/QuantumOffer";
import BundleOffer from "@/components/temple/BundleOffer";

const INCLUDED = [
  "All 13 Mastery Guide rituals, from Root Resonance to Embodied Enlightenment",
  "Every step-by-step activation protocol and 3-week practice rhythm",
  "Partnered practice, troubleshooting, success signs and daily anchor rituals",
  "Each gate’s closing blessing from Goddess Ariel",
  "Lifetime access — one payment, no subscription",
];

export default function Initiate() {
  const [params] = useSearchParams();
  const product = params.get("product");
  if (product === "bundle") return <BundleOffer />;
  return product === "quantum" ? <QuantumOffer /> : <GatesOffer />;
}

function GatesOffer() {
  const { user } = useAuth();

  if (user?.initiate) {
    return (
      <>
      <main data-testid="initiate-page" className="min-h-[80vh] flex items-center justify-center px-5 pt-28 pb-20 text-center">
        <div className="w-full">
          <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">Welcome, initiate</p>
          <h1 className="font-display text-5xl sm:text-6xl mt-6">All 13 gates are <em className="text-[#D9B47A]">open</em> to you.</h1>
          <Link to="/gates/root-resonance" data-testid="initiate-begin" className="mt-10 inline-flex rounded-full bg-[#D9B47A] text-[#0A0706] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em]">Begin at Gate 01</Link>
          <Link to="/blessings" data-testid="initiate-blessings-link" className="mt-10 ml-3 inline-flex rounded-full border border-[#D9B47A]/50 px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">Your blessings</Link>
          <ReflectionForm />
        </div>
      </main>
      <ReflectionWall />
      </>
    );
  }

  return (
    <>
    <main data-testid="initiate-page" className="max-w-[1440px] mx-auto px-5 sm:px-10 pt-32 sm:pt-40 pb-28 grid lg:grid-cols-12 gap-16">
      <div className="lg:col-span-6">
        <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">13 Gates to Ecstasy · Become an Initiate</p>
        <h1 className="font-display text-[clamp(3rem,7vw,6.5rem)] leading-[0.95] mt-6">
          {["Every gate.", "Every ritual."].map((w, k) => (
            <span key={w} className="block overflow-hidden pb-[0.06em]">
              <motion.span className={`block ${k ? "italic text-[#D9B47A]" : ""}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.2, delay: 0.1 + k * 0.12, ease }}>{w}</motion.span>
            </span>
          ))}
        </h1>
        <p className="mt-8 text-lg text-[#CFC2B6] max-w-xl leading-relaxed">The book gives you the map. The Mastery Guides give you the practice. Initiates receive the complete ritual for each of the 13 Gates to Ecstasy.</p>
        <Reveal className="mt-10 space-y-4">
          {INCLUDED.map((t) => (
            <p key={t} className="flex gap-4 text-[#DCCFC3]"><Check className="w-5 h-5 text-[#C2414F] shrink-0 mt-0.5" />{t}</p>
          ))}
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-2">
          {GATES.map((g, k) => (
            <Link key={g.name} to={`/gates/${slugify(g.name)}`} className={`rounded-full border px-3 py-1.5 text-[12px] ${k === 4 ? "border-[#D9B47A] text-[#D9B47A]" : "border-[#D9B47A]/20 text-[#A8998C]"}`}>
              {String(k + 1).padStart(2, "0")} {g.name}{k === 4 ? " · free sample" : ""}
            </Link>
          ))}
        </div>
      </div>

      <div className="lg:col-span-5 lg:col-start-8">
        <CheckoutCard product="gates" already="/gates/root-resonance" />
      </div>
    </main>
    <ReflectionWall />
    </>
  );
}
